import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { Overview } from './tabContent/Overview'
import { Events } from './tabContent/Events'
import { Initiatives } from './tabContent/Initiatives'
import { DevelopmentAreas } from './tabContent/DevelopmentAreas'
import { Concepts } from './tabContent/Concepts'
import useMediaQuery from '@app/util/useMediaQuery'
import { useParamState } from '@app/util/hooks/useParamState'
import { ServiceProviderViewModel } from 'functions/app/service-provider/view'
import {
  SERVICE_PROVIDER_FEATURES,
  type ServiceProviderFeatures,
} from '@schema/featureToggle'
import {
  isFeatureEnabled,
  isFeatureAvailable,
} from '@app/util/featureToggle'

import { Contact } from './Contact'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { ArticleSection } from '@app/components/blocks/ArticleSection'

const tabs = {
  ...SERVICE_PROVIDER_FEATURES,
  contact: 'contact',
} as const

export const TabContent = ({
  serviceProvider,
}: {
  serviceProvider: ServiceProviderViewModel
}) => {
  const appTranslations = useTranslations().app
  const translations = appTranslations.serviceProviderPage
  const [tab, setTab] = useParamState(tabs, SERVICE_PROVIDER_FEATURES.overview)
  const isMobile = useMediaQuery()
  const paddingRight = isMobile ? '0rem' : '4.75rem'

  const isFeatureEnabledFor = (identifier: ServiceProviderFeatures) =>
    isFeatureEnabled(serviceProvider.featureToggles, identifier)

  const showKnowledgeHubTab = isFeatureAvailable(
    serviceProvider.featureToggles,
    SERVICE_PROVIDER_FEATURES.knowledgeHub,
    [serviceProvider.knowledgeHubModules],
  )

  const showCurrentTab = isFeatureAvailable(
    serviceProvider.featureToggles,
    SERVICE_PROVIDER_FEATURES.current,
    [serviceProvider.stories, serviceProvider.news, serviceProvider.blogs],
  )

  return (
    <Column
      alignItems="stretch"
      gap={isMobile ? '1rem' : '2rem'}
      style={{ paddingRight: paddingRight }}
    >
      <TabsGroup
        selectedTab={tab}
        onSelectTab={setTab}
        tabs={[
          {
            label: translations.overview,
            value: SERVICE_PROVIDER_FEATURES.overview,
            isVisible: isFeatureEnabledFor(SERVICE_PROVIDER_FEATURES.overview),
          },
          {
            label: translations.events,
            value: SERVICE_PROVIDER_FEATURES.events,
            isVisible: isFeatureEnabledFor(SERVICE_PROVIDER_FEATURES.events),
          },
          {
            label: translations.developmentArea,
            value: SERVICE_PROVIDER_FEATURES.developmentAreas,
            isVisible: isFeatureEnabledFor(
              SERVICE_PROVIDER_FEATURES.developmentAreas,
            ),
          },
          {
            label: translations.concepts,
            value: SERVICE_PROVIDER_FEATURES.concepts,
            isVisible: isFeatureEnabledFor(SERVICE_PROVIDER_FEATURES.concepts),
          },
          {
            label: translations.initiatives,
            value: SERVICE_PROVIDER_FEATURES.initiatives,
            isVisible: isFeatureEnabledFor(
              SERVICE_PROVIDER_FEATURES.initiatives,
            ),
          },
          {
            label: translations.knowledgeHub,
            value: tabs.knowledgeHub,
            isVisible: showKnowledgeHubTab,
          },
          {
            label: translations.current,
            value: tabs.current,
            isVisible: showCurrentTab,
          },
          {
            label: appTranslations.common.contact,
            value: tabs.contact,
            isVisible: isMobile,
          },
        ]}
      />
      {match(tab)
        .with(SERVICE_PROVIDER_FEATURES.overview, () =>
          isFeatureEnabledFor(SERVICE_PROVIDER_FEATURES.overview) ? (
            <Overview
              key={SERVICE_PROVIDER_FEATURES.overview}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(SERVICE_PROVIDER_FEATURES.events, () =>
          isFeatureEnabledFor(SERVICE_PROVIDER_FEATURES.events) ? (
            <Events
              key={SERVICE_PROVIDER_FEATURES.events}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(SERVICE_PROVIDER_FEATURES.developmentAreas, () =>
          isFeatureEnabledFor(SERVICE_PROVIDER_FEATURES.developmentAreas) ? (
            <DevelopmentAreas
              key={SERVICE_PROVIDER_FEATURES.developmentAreas}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(SERVICE_PROVIDER_FEATURES.concepts, () =>
          isFeatureEnabledFor(SERVICE_PROVIDER_FEATURES.concepts) ? (
            <Concepts
              key={SERVICE_PROVIDER_FEATURES.concepts}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(SERVICE_PROVIDER_FEATURES.initiatives, () =>
          isFeatureEnabledFor(SERVICE_PROVIDER_FEATURES.initiatives) ? (
            <Initiatives
              key={SERVICE_PROVIDER_FEATURES.initiatives}
              serviceProvider={serviceProvider}
            />
          ) : null,
        )
        .with(tabs.contact, () => (
          <Contact key={tabs.contact} serviceProvider={serviceProvider} />
        ))
        .with(tabs.knowledgeHub, () =>
          showKnowledgeHubTab ? (
            <KnowledgeHubModulesSection
              key={tabs.knowledgeHub}
              modules={serviceProvider.knowledgeHubModules ?? []}
            />
          ) : null,
        )
        .with(tabs.current, () =>
          showCurrentTab ? (
            <ArticleSection
              key={tabs.current}
              stories={serviceProvider.stories ?? []}
              news={serviceProvider.news ?? []}
              blogs={serviceProvider.blogs ?? []}
            />
          ) : null,
        )
        .exhaustive()}
    </Column>
  )
}
